/**
 * Add RFP Notes worksheet to workbook
 */

import ExcelJS from "exceljs";
import { getRfpNote } from "@platform/plan-grid-core";

import type { AxiosInstance } from "axios";

const COLUMN_COUNT = 10; // A–J
const MIN_LAST_ROW = 50; // the notes box spans A2:J50 at minimum

const BOX_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "medium" },
  bottom: { style: "medium" },
  left: { style: "medium" },
  right: { style: "medium" },
};

// Draw a border around a merged box. Interior gridlines don't render inside a
// merged range, so bordering every cell in the range just outlines the box.
const borderBox = (
  worksheet: ExcelJS.Worksheet,
  firstRow: number,
  lastRow: number,
): void => {
  for (let row = firstRow; row <= lastRow; row += 1) {
    for (let col = 1; col <= COLUMN_COUNT; col += 1) {
      worksheet.getCell(row, col).border = BOX_BORDER;
    }
  }
};

export const addNotesWorksheet = async (
  workbook: ExcelJS.Workbook,
  ecoRfpId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  const { content } = await getRfpNote(ecoRfpId, axiosInstance);

  if (!content) {
    return;
  }

  const worksheet = workbook.addWorksheet("RFP Notes");
  for (let col = 1; col <= COLUMN_COUNT; col += 1) {
    worksheet.getColumn(col).width = 12;
  }

  // Title bar — navy fill, white text; matches the legacy RFP template.
  worksheet.mergeCells(1, 1, 1, COLUMN_COUNT);
  const title = worksheet.getCell("A1");
  title.value = "RFP Notes & Requirements";
  title.font = { bold: true, size: 24, color: { argb: "FFFFFFFF" } };
  title.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF002060" },
  };
  title.alignment = { horizontal: "center", vertical: "middle" };
  // Set the row height explicitly — Windows Excel won't grow row 1 to fit the
  // 24pt title on its own, and would otherwise clip it vertically.
  worksheet.getRow(1).height = 32;
  borderBox(worksheet, 1, 1);

  // Notes box — a fixed A2:J50 box, grown a row per line for longer notes.
  // A fixed box (not an auto-fit row height) is what renders consistently on
  // Windows Excel, where a merged cell's wrapText does not grow the row.
  const lastRow = Math.max(MIN_LAST_ROW, content.split("\n").length + 1);
  worksheet.mergeCells(2, 1, lastRow, COLUMN_COUNT);
  const notes = worksheet.getCell("A2");
  notes.value = content;
  notes.font = { size: 12 };
  notes.alignment = { horizontal: "left", vertical: "top", wrapText: true };
  borderBox(worksheet, 2, lastRow);
};
